<template>
    <Modal ref="modal" class="returned-products-modal" @opened="onModalOpen" @closed="onModalClose">
        <template #header="sp">
            <div class="h3">Retours produits</div>

            <div class="form-check mt-1">
                <input class="form-check-input" type="checkbox" name="lock" id="lock" value="1" v-model="lockAfterSave">
                <label class="form-check-label small" for="lock">Verrouiller et facturer</label>
            </div>
        </template>
        <template #body="sp">
            <PresenceChannelUsers :channel="`employees.order-${order.id}.returned-products-modal`" v-slot="sp" >
                <div v-if="sp.users.length > 1" class="alert alert-warning w-100 p-1 m-0 mb-1">
                    Plusieurs personnes sont sur ce formulaire et peuvent écraser vos modifications : {{ sp.names }}
                </div>
            </PresenceChannelUsers>

            <table class="table table-striped table-sm">
                <tbody>
                    <template v-for="mainCategory in productsByMainCategory">
                        <tr class="bg-dark text-white" :key="mainCategory.category.id">
                            <th colspan="2">{{ mainCategory.category.name }}</th>
                        </tr>
                        <template v-for="infosCategory in mainCategory.productsBySubCategory">
                            <tr v-for="(productWithInputs, sku) in infosCategory.products" :key="infosCategory.category.id+'-'+sku">
                                <td>
                                    <label :for="'returned-' + sku">{{ productWithInputs.product.tiny_name }}</label>
                                </td>
                                <td>
                                    <InputIntegerPositive class="form-control form-control-sm" :allow-zero="true" :value-when-empty="0" :id="'returned-' + sku" v-model="quantities[sku]" />
                                </td>
                            </tr>
                        </template>
                    </template>
                </tbody>
            </table>
        </template>
        <template #footer>
            <ErrorsZone :errors="errors" />

            <div v-if="valuesUpdatedByOtherUser" class="alert alert-warning">
                Attention, les retours ont été modifiés par un autre utilisateur. Vérifiez que vos modifications sont toujours présentes avant d'enregistrer.
            </div>

            <div class="d-flex justify-content-between align-items-start m-0 p-0 w-100">
                <table class="table table-sm m-0 mr-4">
                    <tr v-for="total in totalsByMainCategory" :key="total.category.id">
                        <th>{{ total.category.name }}</th>
                        <td>{{ total.quantity }}</td>
                    </tr>
                </table>

                <button type="button" class="btn text-nowrap" :class="[valuesUpdatedByOtherUser ? 'btn-warning' : 'btn-success']" @click="onClickButton()">
                    <i class="fas fa-save"></i> Enregistrer
                </button>
            </div>
        </template>
    </Modal>
</template>

<script>
    import Modal from "../../Modal";
    import ErrorsZone from "../../ErrorsZone";

    import {usePostRequest} from "../../compositions/axios/postRequest";
    import InputIntegerPositive from "../../InputIntegerPositive"
    import PresenceChannelUsers from "../../PresenceChannelUsers"

    export default {
        name: 'ReturnedProductsModal',

        components: {
            PresenceChannelUsers,
            ErrorsZone,
            InputIntegerPositive,
            Modal,
        },

        setup() {
            const {
                loading: sending,
                errors,
                post
            } = usePostRequest();

            return {
                sending,
                errors,
                post
            };
        },

        props: {
            order: {
                type: Object,
                required: true,
            },
            productsByMainCategory: {
                type: Array,
                required: true,
            },
        },

        data() {
            this.initializeQuantities();

            return {
                lockAfterSave: false,
                quantities: {},
                valuesUpdatedByOtherUser: false,
            };
        },

        computed: {
            totalsByMainCategory() {
                let totals = [];
                for (const categoryWithProducts of this.productsByMainCategory) {
                    let quantity = 0;
                    for (const sku in categoryWithProducts.products) {
                        quantity += this.quantities[sku] || 0;
                    }
                    totals.push({
                        category: categoryWithProducts.category,
                        quantity: quantity,
                    })
                }
                return totals;
            },
        },

        watch: {
            order(newValue, oldValue) {
                const oldQuantities = _.mapValues(_.keyBy(oldValue.returned_products, 'sku'), 'pivot.quantity');
                const newQuantities = _.mapValues(_.keyBy(newValue.returned_products, 'sku'), 'pivot.quantity');

                let shouldUpdate = false;
                const skus = _.union(Object.keys(oldQuantities), Object.keys(newQuantities));
                for (const sku of skus) {
                    if ((newQuantities[sku] || 0) !== (oldQuantities[sku] || 0)) {
                        shouldUpdate = true;
                    }
                }
                if (shouldUpdate) {
                    this.initializeQuantities();
                    this.valuesUpdatedByOtherUser = true;
                }
            },
        },

        methods: {
            showModal() {
                this.$refs.modal.showModal();
            },

            closeModal() {
                this.$refs.modal.closeModal();
            },

            onModalOpen() {
                this.errors = null;
                this.initializeQuantities();
                this.valuesUpdatedByOtherUser = false;
            },

            onModalClose() {
                this.errors = null;
                this.lockAfterSave = false;
            },

            async onClickButton() {
                let returnedProductsArray = [];
                for (const sku in this.quantities) {
                    returnedProductsArray.push({
                        sku,
                        quantity: this.quantities[sku],
                    });
                }

                const response = await this.post('/espace-employes/commandes/preparation/commande/' + this.order.id + '/retours-produits', {
                    lock: this.lockAfterSave,
                    products: returnedProductsArray,
                });

                if (!this.errors) {
                    this.closeModal();

                    if (!this.$root.echoConnectedWihoutErrors) {
                        window.location.reload();
                    }
                }
            },

            initializeQuantities() {
                let tmpQuantities = {};
                for (const preparedProduct of this.order.prepared_products) {
                    tmpQuantities[preparedProduct.sku] = 0;
                }
                for (const returnedProduct of this.order.returned_products) {
                    tmpQuantities[returnedProduct.sku] = returnedProduct.pivot.quantity;
                }
                this.quantities = tmpQuantities;
            },
        },
    }
</script>

<style lang="scss">
    .returned-products-modal {
        label {
            margin: 0;
        }

        table td {
            vertical-align: middle;
        }

        input {
            text-align: right;
        }

        .modal-body {
            padding: 0;
        }

        .modal-footer {
            padding: 0;
        }
    }
</style>
